import { useMemo } from 'react';
import { useAppSelector } from './redux';
import { hasDatasetAccess, hasModuleAccess, getAccessibleDatasets } from '../helpers/functions/licenseHelpers';

export interface DatasetAccessInfo {
  hasAccess: boolean;
  canView: boolean;
  canFilter: boolean;
  canVisualize: boolean;
  reason?: string;
}

export interface VisualizationAccessInfo {
  canCreateCrossplot: boolean;
  canCreateHistogram: boolean;
  canCreateGrid: boolean;
  canCreateProductionPlot: boolean;
  canUseCcsFeatures: boolean;
  canUseAnalogueFeatures: boolean;
}

export const useLicenseAccess = () => {
  const { licenseInfo } = useAppSelector((state) => state.userStateSlice);

  // Dataset access mapping
  const datasetMapping = useMemo(() => ({
    "0": ["subsurface_with_grid", "subsurface_no_grid"], // Subsurface Assets
    "1": ["infrastructure"], // Global Pipeline Networks
    "2": ["emissions"], // CO2 Emitters
    "3": ["ccs_projects"], // CCS Project
    "4": ["blocks"], // Blocks
    "5": ["licences"], // Licences
    "6": ["windfarms"], // Windfarms
    "7": ["aquifers"], // Aquifers
  }), []);

  // Check access for a specific dataset by ID
  const checkDatasetAccess = useMemo(() => 
    (datasetId: string): DatasetAccessInfo => {
      if (!licenseInfo) {
        return {
          hasAccess: false,
          canView: false,
          canFilter: false,
          canVisualize: false,
          reason: 'No license information available'
        };
      }

      const requiredDatasets = datasetMapping[datasetId as keyof typeof datasetMapping];
      
      if (!requiredDatasets) {
        // Unknown dataset - allow by default for backward compatibility
        return {
          hasAccess: true,
          canView: true,
          canFilter: true,
          canVisualize: true
        };
      }

      const hasAnyRequiredDataset = requiredDatasets.some(dataset => 
        hasDatasetAccess(licenseInfo, dataset)
      );

      if (!hasAnyRequiredDataset) {
        return {
          hasAccess: false,
          canView: false,
          canFilter: false,
          canVisualize: false,
          reason: `No access to required datasets: ${requiredDatasets.join(', ')}`
        };
      }

      // Additional module checks for specific datasets
      if (["1", "2", "6"].includes(datasetId)) {
        const hasCcsModule = hasModuleAccess(licenseInfo, 'ccs_module');
        if (!hasCcsModule) {
          return {
            hasAccess: false,
            canView: false,
            canFilter: false,
            canVisualize: false,
            reason: 'CCS module access required'
          };
        }
      }

      return {
        hasAccess: true,
        canView: true,
        canFilter: true,
        canVisualize: true
      };
    }, [licenseInfo, datasetMapping]);

  // Check visualization access
  const visualizationAccess = useMemo((): VisualizationAccessInfo => {
    if (!licenseInfo) {
      return {
        canCreateCrossplot: false,
        canCreateHistogram: false,
        canCreateGrid: false,
        canCreateProductionPlot: false,
        canUseCcsFeatures: false,
        canUseAnalogueFeatures: false
      };
    }

    const hasSubsurfaceAccess = hasDatasetAccess(licenseInfo, 'subsurface_with_grid') || 
                               hasDatasetAccess(licenseInfo, 'subsurface_no_grid');
    const hasGridAccess = hasDatasetAccess(licenseInfo, 'subsurface_with_grid');
    const hasCcsModule = hasModuleAccess(licenseInfo, 'ccs_module');
    const hasAnalogueModule = hasModuleAccess(licenseInfo, 'analogue_finder');

    return {
      canCreateCrossplot: hasSubsurfaceAccess,
      canCreateHistogram: hasSubsurfaceAccess,
      canCreateGrid: hasGridAccess,
      canCreateProductionPlot: hasSubsurfaceAccess,
      canUseCcsFeatures: hasCcsModule,
      canUseAnalogueFeatures: hasAnalogueModule
    };
  }, [licenseInfo]);

  // Get all accessible dataset IDs
  const accessibleDatasets = useMemo(() => 
    getAccessibleDatasets(licenseInfo || {}), [licenseInfo]);

  // Check if any dataset access exists
  const hasAnyDatasetAccess = useMemo(() => 
    accessibleDatasets.length > 0, [accessibleDatasets]);

  // Block unauthorized actions
  const blockUnauthorizedAction = (datasetId: string, action = 'access') => {
    const access = checkDatasetAccess(datasetId);
    if (!access.hasAccess) {
      console.warn(`Unauthorized ${action} attempt for dataset ${datasetId}: ${access.reason}`);
      return false;
    }
    return true;
  };

  return {
    licenseInfo,
    checkDatasetAccess,
    visualizationAccess,
    accessibleDatasets,
    hasAnyDatasetAccess,
    blockUnauthorizedAction,
    
    // Convenience methods for common checks
    canAccessSubsurface: () => hasDatasetAccess(licenseInfo || {}, 'subsurface_with_grid') || 
                              hasDatasetAccess(licenseInfo || {}, 'subsurface_no_grid'),
    canAccessCcs: () => hasDatasetAccess(licenseInfo || {}, 'ccs_projects'),
    canAccessInfrastructure: () => hasDatasetAccess(licenseInfo || {}, 'infrastructure'),
    canUseCcsModule: () => hasModuleAccess(licenseInfo || {}, 'ccs_module'),
    canUseAnalogueModule: () => hasModuleAccess(licenseInfo || {}, 'analogue_finder'),
  };
};

export default useLicenseAccess; 